/**
 * Andamento del valore di un portafoglio (F-116, F-183): titoli, titoli +
 * liquidità e patrimonio **al netto dei versamenti**, su un asse solo.
 *
 * ## Perché un asse solo
 *
 * Prima le due linee avevano assi diversi con la stessa scala, ciascuna partita
 * dal proprio primo valore. Le pendenze erano confrontabili, ma la posizione
 * no: il patrimonio, che contiene i titoli, finiva *sotto* di loro appena i
 * titoli crescevano più in fretta della cassa — cioè ogni volta che si
 * compra. Un grafico in cui la parte supera il tutto si legge al contrario.
 * Sullo stesso asse il patrimonio sta sempre sopra, e la distanza fra le due
 * linee è la liquidità.
 *
 * ## Al netto dei versamenti
 *
 * Il patrimonio sale anche quando si versano soldi propri, che non sono un
 * guadagno. La terza linea toglie ogni versamento dal giorno in cui arriva (e
 * riaggiunge ogni prelievo); dividendi e interessi restano, perché sono
 * rendimento. Il calcolo è del backend (`portfolio-history.ts`): qui solo
 * coordinate. La fascia fra patrimonio e linea compensata è il denaro versato
 * nel periodo.
 *
 * ⚠️ Come in `ValueTrendChart`, i decimali diventano `number` solo per le
 * coordinate (ADR-0006); ciò che si legge a schermo parte dalla stringa.
 */

import { useId, useMemo, useState } from 'react';
import { formatCurrency, formatDate, signOf } from '@/lib/format';
import { Card, Spinner } from '@/ui';
import { niceBounds, useMeasuredWidth } from '@/ui/chart-scale';
import '@/ui/value-trend-chart.css';
import { usePortfolioHistory, type SnapshotPoint } from './api';
import { Segmented } from './Segmented';

export type SeriesKey = 'netOfFlows' | 'total' | 'securities';

const SERIES: readonly { key: SeriesKey; label: string; read: (point: SnapshotPoint) => string | null }[] = [
  { key: 'netOfFlows', label: 'Al netto dei versamenti', read: (point) => point.totalNetOfFlows },
  { key: 'total', label: 'Titoli + liquidità', read: (point) => point.totalWithCash },
  { key: 'securities', label: 'Titoli', read: (point) => point.totalValue },
];

const PERIODS: readonly { value: number | null; label: string; title: string }[] = [
  { value: 30, label: '30 G', title: 'Ultimi 30 giorni' },
  { value: 90, label: '90 G', title: 'Ultimi 90 giorni' },
  { value: null, label: 'Tutto', title: 'Tutto lo storico' },
];

const DEFAULT_DAYS = 90;
const ALL_VISIBLE: Record<SeriesKey, boolean> = { netOfFlows: true, total: true, securities: true };

const VIEW_HEIGHT = 260;
const TICK_COUNT = 4;
/** In alto c'è posto per le etichette dei versamenti. */
const PADDING_TOP = 26;
const PADDING_BOTTOM = 28;
const PADDING_LEFT = 64;
const PADDING_RIGHT = 16;
/** Sotto questa distanza due etichette di versamento si accavallano: la seconda tace. */
const FLOW_LABEL_GAP = 64;

type Plotted = { x: number; y: number };

function numeric(value: string | null): number | null {
  if (value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function signedCurrency(value: string, currency: string): string {
  const formatted = formatCurrency(value, currency);
  return signOf(value) === 'positive' ? `+${formatted}` : formatted;
}

const axisFormat = (value: number): string =>
  new Intl.NumberFormat('it-IT', {
    notation: Math.abs(value) >= 10_000 ? 'compact' : 'standard',
    maximumFractionDigits: Math.abs(value) >= 10_000 ? 1 : 0,
  }).format(value);

type ChartProps = {
  points: readonly SnapshotPoint[];
  currency: string;
  label: string;
  visible: Record<SeriesKey, boolean>;
};

export function PortfolioValueChart({ points, currency, label, visible }: ChartProps) {
  const titleId = useId();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [figureRef, width] = useMeasuredWidth();

  const plotWidth = Math.max(1, width - PADDING_LEFT - PADDING_RIGHT);
  const plotHeight = VIEW_HEIGHT - PADDING_TOP - PADDING_BOTTOM;

  const rows = useMemo(
    () =>
      points
        .map((point) => ({ point, time: new Date(point.snapshotDate).getTime() }))
        .filter((row) => Number.isFinite(row.time) && numeric(row.point.totalValue) !== null),
    [points],
  );

  const scene = useMemo(() => {
    if (rows.length < 2) return null;

    const tMin = rows[0]!.time;
    const tSpan = rows[rows.length - 1]!.time - tMin;
    const xOf = (time: number): number =>
      PADDING_LEFT + (tSpan === 0 ? plotWidth / 2 : ((time - tMin) / tSpan) * plotWidth);

    const shown = SERIES.filter((series) => visible[series.key]);
    const values = rows.flatMap((row) =>
      shown.map((series) => numeric(series.read(row.point))).filter((value): value is number => value !== null),
    );
    const { lower, upper } = values.length > 0 ? niceBounds(Math.min(...values), Math.max(...values)) : { lower: 0, upper: 1 };
    const range = upper - lower || 1;
    const yOf = (value: number): number => PADDING_TOP + plotHeight - ((value - lower) / range) * plotHeight;

    // Un punto per riga, `null` dove la serie non ha valore: la stessa riga
    // indica la stessa data su tutte le linee, così il passaggio del mouse
    // trova i tre valori dello stesso giorno con un indice solo.
    const plotted = Object.fromEntries(
      SERIES.map((series) => [
        series.key,
        rows.map((row): Plotted | null => {
          const value = numeric(series.read(row.point));
          return value === null ? null : { x: xOf(row.time), y: yOf(value) };
        }),
      ]),
    ) as Record<SeriesKey, (Plotted | null)[]>;

    let lastLabelX = Number.NEGATIVE_INFINITY;
    const flows = rows.flatMap((row, index) => {
      if (index === 0 || signOf(row.point.netFlow) === 'zero') return [];
      const x = xOf(row.time);
      const showLabel = x - lastLabelX >= FLOW_LABEL_GAP;
      if (showLabel) lastLabelX = x;
      return [{ x, date: row.point.snapshotDate, amount: row.point.netFlow, showLabel }];
    });

    return {
      x: rows.map((row) => xOf(row.time)),
      plotted,
      flows,
      ticks: Array.from({ length: TICK_COUNT + 1 }, (_, index) => ({
        value: lower + ((upper - lower) / TICK_COUNT) * index,
        y: PADDING_TOP + plotHeight - (index / TICK_COUNT) * plotHeight,
      })),
    };
  }, [rows, visible, plotWidth, plotHeight]);

  if (rows.length === 0) {
    return (
      <p className="trend__empty">
        Nessuno snapshot con un valore: l'andamento comparirà dopo il primo import valorizzato.
      </p>
    );
  }

  if (!scene) {
    const only = rows[0]!.point;
    return (
      <div className="trend__single">
        <span className="trend__single-value">
          {formatCurrency(only.totalWithCash ?? only.totalValue, currency)}
        </span>
        <span className="trend__single-date">al {formatDate(only.snapshotDate)}</span>
        <p className="trend__single-hint">
          Un solo snapshot: l'andamento richiede almeno due import in date diverse.
        </p>
      </div>
    );
  }

  const bottom = PADDING_TOP + plotHeight;
  const pathOf = (samples: readonly (Plotted | null)[]): string =>
    samples
      .filter((sample): sample is Plotted => sample !== null)
      .map((sample, index) => `${index === 0 ? 'M' : 'L'} ${sample.x} ${sample.y}`)
      .join(' ');

  const securities = scene.plotted.securities.filter((sample): sample is Plotted => sample !== null);
  const securitiesArea =
    securities.length > 1
      ? `${pathOf(securities)} L ${securities[securities.length - 1]!.x} ${bottom} L ${securities[0]!.x} ${bottom} Z`
      : null;

  // La fascia del versato: patrimonio in andata, linea compensata al ritorno,
  // solo sui giorni in cui esistono entrambe.
  const bandPairs = scene.plotted.total
    .map((top, index) => ({ top, base: scene.plotted.netOfFlows[index] ?? null }))
    .filter((pair): pair is { top: Plotted; base: Plotted } => pair.top !== null && pair.base !== null);
  const band =
    visible.total && visible.netOfFlows && bandPairs.length > 1
      ? `${pathOf(bandPairs.map((pair) => pair.top))} ${[...bandPairs]
          .reverse()
          .map((pair) => `L ${pair.base.x} ${pair.base.y}`)
          .join(' ')} Z`
      : null;

  const hovered = hoveredIndex === null ? null : rows[hoveredIndex]?.point ?? null;
  const hoveredX = hoveredIndex === null ? 0 : scene.x[hoveredIndex]!;
  const first = rows[0]!.point;
  const last = rows[rows.length - 1]!.point;

  const handleMove = (event: React.MouseEvent<SVGSVGElement>): void => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * width;
    let nearest = 0;
    let distance = Number.POSITIVE_INFINITY;
    scene.x.forEach((candidate, index) => {
      const delta = Math.abs(candidate - x);
      if (delta < distance) {
        distance = delta;
        nearest = index;
      }
    });
    setHoveredIndex(nearest);
  };

  return (
    <figure className="trend pvc" ref={figureRef as React.RefObject<HTMLElement>}>
      <svg
        className="trend__svg pvc__svg"
        viewBox={`0 0 ${width} ${VIEW_HEIGHT}`}
        role="img"
        aria-labelledby={titleId}
        onMouseMove={handleMove}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <title id={titleId}>{label}</title>

        {scene.ticks.map((tick) => (
          <g key={tick.y}>
            <line className="trend__grid" x1={PADDING_LEFT} x2={width - PADDING_RIGHT} y1={tick.y} y2={tick.y} />
            <text className="trend__axis-label" x={PADDING_LEFT - 10} y={tick.y + 4} textAnchor="end">
              {axisFormat(tick.value)}
            </text>
          </g>
        ))}

        {band && <path className="pvc__band" d={band} />}

        {scene.flows.map((flow) => (
          <g key={flow.date} className={signOf(flow.amount) === 'positive' ? 'pvc__flow' : 'pvc__flow pvc__flow--out'}>
            <line x1={flow.x} x2={flow.x} y1={PADDING_TOP} y2={bottom} />
            {flow.showLabel && (
              <text x={flow.x} y={PADDING_TOP - 10} textAnchor="middle">
                {signedCurrency(flow.amount, currency)}
              </text>
            )}
          </g>
        ))}

        {visible.securities && securitiesArea && <path className="trend__area" d={securitiesArea} />}
        {visible.securities && <path className="trend__line pvc__line--securities" d={pathOf(scene.plotted.securities)} />}
        {visible.total && <path className="trend__line pvc__line--total" d={pathOf(scene.plotted.total)} />}
        {visible.netOfFlows && <path className="trend__line pvc__line--net" d={pathOf(scene.plotted.netOfFlows)} />}

        {hoveredIndex !== null && (
          <g>
            <line className="trend__crosshair" x1={hoveredX} x2={hoveredX} y1={PADDING_TOP} y2={bottom} />
            {SERIES.filter((series) => visible[series.key]).map((series) => {
              const sample = scene.plotted[series.key][hoveredIndex];
              return sample ? (
                <circle key={series.key} className={`pvc__dot pvc__dot--${series.key}`} cx={sample.x} cy={sample.y} r={4.5} />
              ) : null;
            })}
          </g>
        )}

        <text className="trend__axis-label" x={scene.x[0]} y={VIEW_HEIGHT - 8} textAnchor="start">
          {formatDate(first.snapshotDate)}
        </text>
        <text className="trend__axis-label" x={scene.x[scene.x.length - 1]} y={VIEW_HEIGHT - 8} textAnchor="end">
          {formatDate(last.snapshotDate)}
        </text>
      </svg>

      {hovered && (
        <div className="trend__tooltip" style={{ left: `${(hoveredX / width) * 100}%` }} role="status">
          <span className="trend__tooltip-date">{formatDate(hovered.snapshotDate)}</span>
          {SERIES.filter((series) => visible[series.key]).map((series) => (
            <span key={series.key} className={`trend__tooltip-value pvc__tip--${series.key}`}>
              {series.label}: {formatCurrency(series.read(hovered), currency)}
            </span>
          ))}
          {signOf(hovered.netFlow) !== 'zero' && hoveredIndex !== 0 && (
            <span className="trend__tooltip-date">
              {signOf(hovered.netFlow) === 'positive' ? 'Versati' : 'Prelevati'} dal punto prima:{' '}
              {signedCurrency(hovered.netFlow, currency)}
            </span>
          )}
        </div>
      )}

      {/* Vista tabellare: l'informazione non è mai solo nella forma del grafico. */}
      <table className="visually-hidden">
        <caption>{label}</caption>
        <thead>
          <tr>
            <th scope="col">Data</th>
            {SERIES.map((series) => (
              <th key={series.key} scope="col">
                {series.label}
              </th>
            ))}
            <th scope="col">Versamenti netti</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ point }) => (
            <tr key={point.snapshotDate}>
              <td>{formatDate(point.snapshotDate)}</td>
              {SERIES.map((series) => (
                <td key={series.key}>{formatCurrency(series.read(point), currency)}</td>
              ))}
              <td>{signedCurrency(point.netFlow, currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

/** La scheda «Andamento del valore» del dettaglio: periodo, linee mostrate, riepilogo e grafico. */
export function PortfolioValueTrend({
  portfolioId,
  portfolioName,
  fallbackCurrency,
}: {
  portfolioId: string;
  portfolioName: string;
  fallbackCurrency: string;
}) {
  const [days, setDays] = useState<number | null>(DEFAULT_DAYS);
  const [visible, setVisible] = useState<Record<SeriesKey, boolean>>(ALL_VISIBLE);
  const historyQuery = usePortfolioHistory(portfolioId, days);
  const history = historyQuery.data;
  const currency = history?.currency ?? fallbackCurrency;
  const visibleCount = SERIES.filter((series) => visible[series.key]).length;

  const toggle = (key: SeriesKey): void => setVisible((current) => ({ ...current, [key]: !current[key] }));

  return (
    <Card
      title="Andamento del valore"
      subtitle="Tutte le linee sullo stesso asse. «Al netto dei versamenti» toglie i soldi versati dal giorno in cui arrivano e riaggiunge i prelievi: sale solo se il portafoglio guadagna (dividendi e interessi compresi). La fascia è il denaro versato nel periodo."
      action={<Segmented label="Periodo" options={PERIODS} value={days} onChange={setDays} />}
    >
      {historyQuery.isPending ? (
        <Spinner />
      ) : (
        <>
          <div className="pvc__toolbar">
            <div className="pvc__chips" role="group" aria-label="Linee mostrate">
              {SERIES.map((series) => (
                <button
                  key={series.key}
                  type="button"
                  className={`pvc__chip pvc__chip--${series.key}`}
                  aria-pressed={visible[series.key]}
                  // L'ultima linea accesa non si spegne: un grafico vuoto non
                  // risponde a nessuna domanda.
                  disabled={visible[series.key] && visibleCount === 1}
                  onClick={() => toggle(series.key)}
                >
                  <span className="pvc__swatch" aria-hidden="true" />
                  {series.label}
                </button>
              ))}
            </div>
            {history && history.points.length > 1 && (
              <p className="pvc__summary">
                Nel periodo: versati <strong>{signedCurrency(history.netFlowsInWindow, currency)}</strong>
                {history.gainInWindow !== null && (
                  <>
                    {' '}· guadagno al netto dei versamenti{' '}
                    <strong className={signOf(history.gainInWindow) === 'negative' ? 'text-negative' : signOf(history.gainInWindow) === 'positive' ? 'text-positive' : ''}>
                      {signedCurrency(history.gainInWindow, currency)}
                    </strong>
                  </>
                )}
              </p>
            )}
          </div>
          <PortfolioValueChart
            label={`Andamento del valore di ${portfolioName}`}
            currency={currency}
            points={history?.points ?? []}
            visible={visible}
          />
        </>
      )}
    </Card>
  );
}
